import React, {useEffect, useState} from "react";
import Plotly, {LayoutAxis, PlotlyHTMLElement} from "plotly.js";
import {AxesConfig, DatasetChartProps, PlotDataEx} from "./interfaces";
import {clipboardIcon, forkIcon} from "./icons";
import {useResizeDetector} from "react-resize-detector";
import {ProgressSpinner} from "primereact/progressspinner";
import Plot from "react-plotly.js";
import {Button} from "primereact/button";
import {toBlob} from "html-to-image";

const commonAxisConfig = {
    showspikes: true,
    spikemode: "toaxis",
    automargin: true,
    tickformat: "f",
} as Partial<LayoutAxis>;

const secondaryAxisConfig = {
    ...commonAxisConfig,
    overlaying: "y",
    showgrid: false,
};

const xAxisCommonConfig = {
    spikemode: "toaxis",
    showspikes: true,
};

const layoutConfig = {
    margin: {
        l: 0,
        r: 0,
        b: 30,
        t: 20,
    },
    hoverlabel: {
        align: "left"
    }
} as Plotly.Layout;

export default function BasicChart({plotData, props}: { plotData: PlotDataEx[], props?: DatasetChartProps }) {
    const {width, height, ref} = useResizeDetector();
    const [traces, setTraces] = useState<Array<Partial<PlotDataEx>>>([]);
    const [axesConfig, setAxesConfig] = useState<AxesConfig>({});
    const [axesGrouped, setAxesGrouped] = useState(props?.toggleTracesGrouping ?? true);

    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    const [renderCount, setRenderCount] = useState(0);

    useEffect(() => {
        setRenderCount(renderCount + 1);
    }, [width, traces, loaded, axesConfig, axesGrouped]);

    useEffect(() => {
        if (traces.length > 0) {
            if (axesGrouped) {
                groupByUnitTraces(traces);
            } else {
                ungroupTraces(traces);
            }
            setTraces(traces);
        }
        setLoaded(true);
    }, [traces, axesGrouped, width]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [plotData]);

    const onRefresh = () => {
        loadData();
    };

    const toggleGroupingHandler = () => {
        setAxesGrouped((axesGrouped) => !axesGrouped);
    };

    const copyChartToClipBoard = (gd: PlotlyHTMLElement, ev: MouseEvent) => {
        const el = gd.firstChild?.firstChild as HTMLDivElement;
        const modebar = el.getElementsByClassName("modebar-container")[0];

        el.removeChild(modebar);

        toBlob(el)
            .then(function (blob) {
                navigator.clipboard
                    .write([
                        new ClipboardItem({
                            "image/png": blob!,
                        }),
                    ])
                    .then((r) =>
                        PF("globalGrowl").renderMessage({
                            summary: "Clipboard",
                            detail: "Chart copied to clipboard",
                            severity: "info",
                        })
                    );

                el.appendChild(modebar);
            })
            .catch(function (error) {
                console.error("Something went wrong!", error);
            });
    };

    const loadData = () => {
        setLoading(true);
        setTraces(plotData);
        setLoading(false);
    };

    const groupByUnitTraces = (data: Array<Partial<PlotDataEx>>) => {
        const unitMap = new Map<string, string>();
        const axes = axesConfig;
        const domain = [0, 1];

        for (let i = 0; i < data.length; i++) {
            const unit = data[i].unit!!;
            const trace = data[i];
            if (!unitMap.has(unit)) {
                const axCount = unitMap.size + 1;

                if (axCount == 1) {
                    unitMap.set(unit, "y");
                    axes.yaxis = createAxisConfig(axCount, unit!, domain);
                } else {
                    unitMap.set(unit, `y${axCount}`);

                    axes[`yaxis${axCount}`] = createAxisConfig(axCount, unit!, domain);
                }
            }

            trace.yaxis = unitMap.get(unit)!!;
        }

        axes.xaxis = {...xAxisCommonConfig, domain: domain} as LayoutAxis;

        setAxesConfig(axes);
    };

    const ungroupTraces = (data: Array<Partial<PlotDataEx>>) => {
        const axes = axesConfig;
        const domain = [0, 1];

        for (let i = 0; i < data.length; i++) {
            const unit = data[i].unit;
            const trace = data[i];
            const axis = i + 1;

            if (axis === 1) {
                axes.yaxis = createAxisConfig(axis, unit!, domain);
            } else {
                axes[`yaxis${axis}`] = createAxisConfig(axis, unit!, domain);
                trace.yaxis = `y${axis}`;
            }
        }

        axes.xaxis = {...xAxisCommonConfig, domain: domain} as LayoutAxis;

        setAxesConfig(axes);
    };

    const createAxisConfig = (axis: number, unit: string, domain: Array<number>): Plotly.LayoutAxis => {
        if (axis === 1) {
            return {
                title: {
                    text: unit,
                    standoff: 10,
                },
                ...commonAxisConfig,
            } as LayoutAxis;
        } else if (axis === 2) {
            return {
                title: {
                    text: unit,
                    standoff: 10,
                },
                side: "right",
                anchor: "x",
                ...secondaryAxisConfig,
            } as LayoutAxis;
        } else {
            let axOffset;
            const pxVal = 100 / width!!;
            const offVal = pxVal * 0.75;

            // Left
            if (axis % 2 == 0) {
                axOffset = ((axis / 2) | 0) * offVal - offVal * 2;
                domain[0] = Math.max(0, axOffset) + offVal;
            }
            // Right
            else {
                axOffset = 1 - ((axis / 2) | 0) * offVal + offVal;

                if (axOffset > 0) {
                    domain[1] = axOffset - offVal - offVal / 3;
                }
            }

            return {
                title: {
                    text: unit,
                    standoff: 4,
                },
                position: axOffset,
                side: "left",
                anchor: "free",
                ...secondaryAxisConfig,
            } as LayoutAxis;
        }
    };

    const getChartHeight = (): number | undefined => {
        if (props?.height != null) {
            return props?.height as number;
        } else if (props?.height ? props?.height <= 0 : false) {
            return height;
        } else {
            return 400;
        }
    };

    // const totalDataPoints = traces?.map((t) => t.x?.length)?.reduce((c, a) => c! + a!, 0);

    const chartConfig: Partial<Plotly.Config> = {
        displaylogo: false,
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
        modeBarButtonsToAdd: [
            {
                title: "Toggle trace grouping",
                name: "Toggle trace grouping",
                icon: forkIcon,
                click: toggleGroupingHandler,
            },
            {
                title: "Copy chart to Clipboard",
                name: "Copy chart to Clipboard",
                icon: clipboardIcon,
                click: copyChartToClipBoard,
            },
        ],
    };

    return (
        <div ref={ref} className="p-m-2" style={{minHeight: "300px", overflow: "hidden"}}>
            {loaded && !props?.hideRefresh && (
                <div className="flex align-items-center p-mx-2">
                    <Button icon="pi pi-refresh" type="button" loading={loading} onClick={onRefresh}
                            className="p-mr-2"/>
                </div>
            )}
            {!loaded || props?.loading ? (
                <div className="flex justify-content-center p-mb-4">
                    <ProgressSpinner style={{width: "20rem", height: "20rem"}} strokeWidth="4" animationDuration="1s"/>
                </div>
            ) : (
                <Plot
                    className="ea-plotly-chart"
                    config={{...chartConfig}}
                    data={[...traces]}
                    layout={{
                        ...axesConfig,
                        ...layoutConfig,
                        legend: props?.legend || {orientation: "h"},
                        xaxis: props?.xaxisName ? {title: {text: props.xaxisName}} : {},
                        width: width!!,
                        height: getChartHeight(),
                        hovermode: "closest"
                    }}
                />
            )}
        </div>
    );
}
